import React, { memo } from "react";
import { useApp } from "@/contexts/AppContext";
import SeverityBar from "@/components/shared/SeverityBar";
import { BriefingButton, ActionsButton } from "@/components/ai/FloatingInsights";

// High-Performance KPI Anchor to prevent LCP layout shifts
const KPI = memo(({ label, value, accent, testid, onClick, clickable = true }) => {
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      type={clickable ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 basis-0 min-w-[120px] px-3 py-1.5 border-l border-hair first:border-l-0 flex flex-col justify-center text-left ${clickable ? "hover:bg-black/[0.035] cursor-pointer" : ""} transition-colors duration-200`}
    >
      <div className="label-micro truncate opacity-60">{label}</div>
      <div className="font-display text-xl lg:text-3xl leading-tight tracking-tight mt-0.5 truncate tabular-nums" style={{ color: accent || "var(--text-primary)" }}>
        {value ?? <span className="opacity-10">0000</span>}
      </div>
    </Comp>
  );
});

function UnifiedInsightRow() {
  const { kpis, severityDist, setSelectedDetail, setActiveTab, patchFilters } = useApp();

  return (
    <div className="flex flex-col border-b border-hair bg-[var(--bg-surface)]" data-testid="unified-insight-row">
      <div className="px-3 py-1.5 border-b border-hair bg-black/[0.01]">
        <SeverityBar data={severityDist} compact />
      </div>
      <div className="flex items-stretch overflow-x-auto bg-[var(--bg-panel)] min-h-[64px] pt-1">
        <KPI
          testid="kpi-total-signals"
          label="Total Signals"
          value={kpis?.total_posts?.toLocaleString()}
          onClick={() => React.startTransition(() => setSelectedDetail({ type: "severity", payload: { severity: "critical", count: kpis?.critical_count } }))}
        />
        <KPI
          testid="kpi-critical-signals"
          label="Critical"
          value={kpis?.critical_count}
          accent="var(--sev-critical)"
          onClick={() => React.startTransition(() => setSelectedDetail({ type: "severity", payload: { severity: "critical", count: kpis?.critical_count } }))}
        />
        <KPI
          testid="kpi-high-risk"
          label="High Risk"
          value={kpis?.high_risk_count}
          accent="var(--sev-high)"
          onClick={() => React.startTransition(() => setSelectedDetail({ type: "severity", payload: { severity: "high", count: kpis?.high_risk_count } }))}
        />

        <KPI
          testid="kpi-avg-sentiment"
          label="Avg Sentiment"
          value={kpis?.avg_sentiment?.toFixed?.(2) ?? kpis?.avg_sentiment}
          accent={(kpis?.avg_sentiment ?? 0) < 0 ? "var(--sev-high)" : "var(--sev-low)"}
          onClick={() => React.startTransition(() => setActiveTab("analysis"))}
        />
        <KPI
          testid="kpi-posts-24h"
          label="Posts (24h)"
          value={kpis?.posts_last_24h}
          clickable={false}
        />

        <div className="flex-1 basis-0 min-w-[80px] border-l border-hair flex items-center justify-center p-1 bg-black/[0.01]">
          <BriefingButton />
        </div>
        <div className="flex-1 basis-0 min-w-[80px] border-l border-hair flex items-center justify-center p-1 bg-black/[0.01]">
          <ActionsButton />
        </div>
      </div>
    </div>
  );
}

export default memo(UnifiedInsightRow);
